import { useState } from "react";

import dayjs from "dayjs";
import { AlertTriangle, ChevronLeft, ChevronRight, Eye } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { getProductionUnitLabel } from "~/constants/units";
import type { ProductionBatchSummary } from "~/types/production";
import {
  PRODUCTION_BATCH_STATUS_COLORS,
  PRODUCTION_BATCH_STATUS_LABELS,
} from "~/types/production";
import { formatCurrency } from "~/utils/helpers";

interface ProductionBatchesTableProps {
  batches: ProductionBatchSummary[];
  isLoading: boolean;
  onViewBatch: (batchId: string) => void;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50];

export function ProductionBatchesTable({
  batches,
  isLoading,
  onViewBatch,
}: ProductionBatchesTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Reset to first page when batches change (e.g., filters applied)
  const totalPages = Math.ceil(batches.length / pageSize);
  const validCurrentPage = Math.min(currentPage, Math.max(1, totalPages));

  if (validCurrentPage !== currentPage) {
    setCurrentPage(validCurrentPage);
  }

  // Paginate the data
  const startIndex = (validCurrentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedBatches = batches.slice(startIndex, endIndex);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (batches.length === 0) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        No se encontraron lotes de producción
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>N° Lote</TableHead>
            <TableHead>Producto</TableHead>
            <TableHead>Línea</TableHead>
            <TableHead>Planta</TableHead>
            <TableHead className="text-center">Alergenos</TableHead>
            <TableHead className="text-center">Cantidad</TableHead>
            <TableHead>Fechas</TableHead>
            <TableHead className="text-right">Costo Est. (CLP)</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="w-[70px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedBatches.map((batch, index) => (
            <TableRow
              key={batch.id}
              className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50"
              style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
              onClick={() => onViewBatch(batch.id)}
            >
              <TableCell className="font-mono font-medium">
                {batch.batch_number}
              </TableCell>
              <TableCell>
                <div>
                  <div className="font-medium">{batch.product_name}</div>
                  {batch.product_sku && (
                    <div className="text-xs text-muted-foreground">
                      {batch.product_sku}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>{batch.assembly_line || "-"}</TableCell>
              <TableCell>{batch.plant || "-"}</TableCell>
              <TableCell className="text-center">
                {batch.contains_allergens ? (
                  <AlertTriangle className="mx-auto h-4 w-4 text-orange-500" />
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell className="text-center">
                <div>
                  <div className="font-medium">
                    {batch.actual_quantity ?? batch.target_quantity}{" "}
                    {getProductionUnitLabel(
                      batch.production_unit,
                      batch.actual_quantity ?? batch.target_quantity
                    )}
                  </div>
                  {batch.actual_quantity !== null &&
                    batch.actual_quantity !== batch.target_quantity && (
                      <div className="text-xs text-muted-foreground">
                        (objetivo: {batch.target_quantity})
                      </div>
                    )}
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm">
                  <div>
                    {dayjs(batch.start_date).format("DD/MM")} -{" "}
                    {dayjs(batch.end_date).format("DD/MM")}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Vence: {dayjs(batch.expiry_date).format("DD/MM/YYYY")}
                  </div>
                </div>
              </TableCell>
              <TableCell className="text-right">
                <div>
                  <div className="font-medium">
                    {batch.actual_cost_clp
                      ? `$${formatCurrency(batch.actual_cost_clp)}`
                      : batch.estimated_cost_clp
                        ? `$${formatCurrency(batch.estimated_cost_clp)}`
                        : "-"}
                  </div>
                  {batch.cost_per_box_clp && (
                    <div className="text-xs text-muted-foreground">
                      ${formatCurrency(batch.cost_per_box_clp)}/
                      {getProductionUnitLabel(batch.production_unit, 1)}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <Badge className={PRODUCTION_BATCH_STATUS_COLORS[batch.status]}>
                  {PRODUCTION_BATCH_STATUS_LABELS[batch.status]}
                </Badge>
              </TableCell>
              <TableCell>
                <Eye className="h-4 w-4 text-muted-foreground" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination controls */}
      <div className="flex items-center justify-between border-t pt-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            Filas por página:
          </span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => {
              setPageSize(Number(value));
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {startIndex + 1}-{Math.min(endIndex, batches.length)} de{" "}
            {batches.length}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={validCurrentPage <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={validCurrentPage >= totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
